/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2019. All Rights Reserved.
 #*
 #*===================================================================
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UserData } from 'app/shared/models/user.model';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { CoachActionService } from '@shared/services/coach-action.service';
import { Constants } from '@shared/models/constants.model';

@Component({
  selector: 'app-profile-video-modal',
  templateUrl: './profile-video-modal.component.html',
  styleUrls: ['./profile-video-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CoachActionService],
})
export class ProfileVideoModalComponent implements OnInit {
  @Input() profile: UserData;
  @Input() clips: any[];
  @Input() currentClipIndex: number;
  @Input() currentVideo: number;
  @Input() isHeadCoach: boolean;
  @Input() currentUser: any;

  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('relatedVideos', { static: true }) relatedVideosContainer;

  autoplay = true;
  selectedVideo: any;

  hideLeftArrow = true;
  hideRightArrow = false;
  navigationButtonsVisible = true;
  videoPlayer: any;

  private leftScroll$: Subject<number> = new Subject();
  private windowSize$: Subject<number> = new Subject();
  private videosTotalWidth: number;
  private resultCoachAction: any;

  private readonly COACH_PING_INTERVAL = 5000;
  private readonly HIDE_TRESHOLD = 1; // hide/show left right buttons treshold

  constructor(private cdr: ChangeDetectorRef, private coachActionsService: CoachActionService) {}

  @HostListener('window:resize', ['$event'])
  onResize($event) {
    this.windowSize$.next($event.target.innerWidth);
  }

  ngOnInit() {
    if (this.currentClipIndex !== undefined && this.currentClipIndex !== null) {
      // if clip index is set
      this.selectedVideo = this.clips[this.currentClipIndex];
    } else if (this.currentVideo) {
      // if current clip is not part of the visible list
      this.selectedVideo = this.currentVideo;
      this.currentClipIndex = -1;
    }

    this.windowSize$.pipe(debounceTime(200)).subscribe(() => {
      this.saveContainerSize();
    });

    this.leftScroll$.pipe(debounceTime(50)).subscribe(() => {
      this.toggleButtons();
    });
  }

  ngAfterViewInit() {
    this.saveContainerSize();
    this.scrollToPlayingVideo();
    this.toggleButtons();
  }

  ngOnDestroy() {
    this.coachActionsService.clearActionInterval();
  }

  close() {
    this.closeModal.emit();
  }

  toggleAutoplay() {
    this.autoplay = !this.autoplay;
  }

  onPlay($event) {
    if (!this.isHeadCoach) {
      return;
    }
    if ($event.issuer === 'ui-seek') {
      return;
    }
    const coachAction = {
      type: Constants.COACH_ACTION_TYPE.VIDEO_PLAYING,
      athlete: {
        id: this.profile.id,
      },
      coach: {
        id: this.currentUser.id,
      },
      asset: {
        id: this.selectedVideo.id,
      },
      duration: 0,
    };
    this.coachActionsService.clearActionInterval();
    this.coachActionsService.setAction(coachAction);
    this.coachActionsService
      .pingCoachAction(this.COACH_PING_INTERVAL)
      .subscribe((resultCoachAction) => (this.resultCoachAction = { ...resultCoachAction, startTime: $event.time }));
  }

  onPause($event) {
    if (!this.isHeadCoach || $event.issuer === 'ui-seek') {
      return;
    }
    this.coachActionsService.clearActionInterval();
    if (this.resultCoachAction?.startTime || this.resultCoachAction?.startTime === 0) {
      this.coachActionsService.setAction({
        ...this.resultCoachAction,
        duration: Math.abs(Math.round($event.time - this.resultCoachAction.startTime)),
      });
      this.coachActionsService.pingCoachAction(this.COACH_PING_INTERVAL, true).subscribe();
    }
  }

  onSeek($event) {
    if (!this.isHeadCoach) {
      return;
    }
    if (
      this.videoPlayer.isPlaying() &&
      (this.resultCoachAction?.startTime || this.resultCoachAction?.startTime === 0)
    ) {
      this.resultCoachAction = {
        ...this.resultCoachAction,
        startTime: $event.seekTarget - Math.abs($event.position - this.resultCoachAction.startTime),
      };
    }
  }

  onError($event) {
    this.coachActionsService.clearActionInterval();
  }

  playbackFinished($event) {
    // if autoplay is on & not last video in list
    this.coachActionsService.clearActionInterval();
    if (this.autoplay && this.clips[this.currentClipIndex + 1]) {
      this.currentClipIndex++;
      this.selectedVideo = this.clips[this.currentClipIndex];
      this.cdr.detectChanges();
      this.scrollToPlayingVideo();
    }
  }

  playVideo(index) {
    this.currentClipIndex = index;
    this.selectedVideo = this.clips[this.currentClipIndex];
    this.scrollToPlayingVideo();
  }

  scrollBack() {
    const scrollingElement = this.relatedVideosContainer.elementRef.nativeElement;
    const partialElementMinWidth = 100;
    const newLeftValue = scrollingElement.scrollLeft - scrollingElement.offsetWidth + partialElementMinWidth;

    scrollingElement.scrollTo({
      left: newLeftValue,
      top: 0,
      behavior: 'smooth',
    });
  }

  scrollForward() {
    const scrollingElement = this.relatedVideosContainer.elementRef.nativeElement;
    const partialElementMinWidth = 100;
    const newLeftValue = scrollingElement.scrollLeft + scrollingElement.offsetWidth - partialElementMinWidth;

    scrollingElement.scrollTo({
      left: newLeftValue,
      top: 0,
      behavior: 'smooth',
    });
  }

  onScroll($event) {
    this.leftScroll$.next($event.target.offsetLeft);
  }

  getPlayerRef(ref) {
    this.videoPlayer = ref.player;
  }

  private scrollToPlayingVideo() {
    if (!this.relatedVideosContainer && this.currentClipIndex === -1) {
      return;
    }

    // if an item is selected then scroll to the selected thumnail

    // current playing element
    const playingVideo = this.relatedVideosContainer.elementRef.nativeElement.querySelector(
      `article:nth-of-type(${this.currentClipIndex + 1})`
    );

    // if playing video not found
    if (!playingVideo) {
      return;
    }

    // current playing element position
    const playingVideoPosition = playingVideo.getBoundingClientRect();

    // container element
    const scrollingParent = this.relatedVideosContainer.elementRef.nativeElement.parentElement;

    // container element position
    const scrollingParentPosition = scrollingParent.getBoundingClientRect();

    // show part of the previous or next element. Minimum number of pixels to show of that element
    const partialElementMinWidth = 100;

    // distance between selected element end and the container end
    const endOfElementOffset =
      playingVideoPosition.left -
      scrollingParentPosition.left +
      playingVideoPosition.width -
      scrollingParentPosition.width;

    // distance between selected element start and the container start
    const startOfElementOffset = playingVideoPosition.left - scrollingParentPosition.left;

    // we want to show at least 100 pixels (partialElementMinWidth) of the previous element and the next element so the user knows to scroll left & right
    let newLeftValue = scrollingParent.scrollLeft;
    if (startOfElementOffset < partialElementMinWidth) {
      newLeftValue = scrollingParent.scrollLeft - (partialElementMinWidth - startOfElementOffset);
    } else if (endOfElementOffset > -1 * partialElementMinWidth) {
      newLeftValue = scrollingParent.scrollLeft + endOfElementOffset + partialElementMinWidth;
    }

    if (newLeftValue !== scrollingParent.scrollLeft) {
      scrollingParent.scrollTo({
        left: newLeftValue,
        top: 0,
        behavior: 'smooth',
      });
    }
  }

  private saveContainerSize() {
    const containerWidth = this.relatedVideosContainer.elementRef.nativeElement.offsetWidth;
    const videosCount = this.relatedVideosContainer.elementRef.nativeElement.children.length;
    // const videoWidth = this.relatedVideosContainer.elementRef.nativeElement.children[0].offsetWidth;
    const videoWidth = videosCount ? this.relatedVideosContainer.elementRef.nativeElement.children[0].offsetWidth : 0;

    this.videosTotalWidth = videosCount * videoWidth;

    this.navigationButtonsVisible = containerWidth < this.videosTotalWidth;
    this.cdr.detectChanges();
  }

  private toggleButtons() {
    // if no related videos
    if (!this.relatedVideosContainer.elementRef.nativeElement.children.length) {
      this.hideLeftArrow = true;
      this.hideRightArrow = true;
      this.cdr.detectChanges();
      return;
    }

    const scrollingElementPosition = this.relatedVideosContainer.elementRef.nativeElement.getBoundingClientRect();

    const firstItem = this.relatedVideosContainer.elementRef.nativeElement.querySelector(`article:first-of-type`);
    const lastItem = this.relatedVideosContainer.elementRef.nativeElement.querySelector(`article:last-of-type`);

    const firstItemPosition = firstItem?.getBoundingClientRect();
    const lastItemPosition = lastItem?.getBoundingClientRect();

    // if the start of the first card is further left than the start of the container that means the whole card is not visible
    this.hideLeftArrow = firstItemPosition?.left >= scrollingElementPosition?.left - this.HIDE_TRESHOLD;
    // if the end of the last card is further right than the end of the container that means the whole card is not visible
    this.hideRightArrow =
      lastItemPosition?.left + lastItemPosition?.width <=
      scrollingElementPosition?.left + scrollingElementPosition?.width + this.HIDE_TRESHOLD;

    this.cdr.detectChanges();
  }
}
